import { useState } from 'react';
import { MessageCircle, X, Send } from 'lucide-react';

export const WhatsAppButton = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [message, setMessage] = useState('');
  
  const phoneNumber = '923119951299'; // 0311-9951299 -> +92-311-9951299
  const defaultMessage = "Hi! I'm interested in renting a car from Amjed Tours";

  const openWhatsApp = (customMessage) => {
    const text = customMessage || message || defaultMessage;
    const url = `https://wa.me/${phoneNumber}?text=${encodeURIComponent(text)}`;
    window.open(url, '_blank');
    setIsOpen(false);
    setMessage('');
  };

  const quickMessages = [
    "I want to book a car",
    "What are your rental terms?",
    "Do you offer with driver service?",
    "Need help with my booking"
  ];

  return (
    <>
      {/* Chat Popup */}
      {isOpen && (
        <div 
          className="fixed bottom-24 right-6 z-50 w-80 bg-white rounded-2xl shadow-2xl border border-border overflow-hidden"
          data-testid="whatsapp-chat-popup"
          style={{ boxShadow: '0 20px 60px rgba(26, 54, 38, 0.2)' }}
        >
          {/* Header */}
          <div className="bg-[#075E54] text-white p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                <MessageCircle className="w-5 h-5" />
              </div>
              <div>
                <p className="font-bold text-sm">Amjed Tours Support</p>
                <p className="text-xs opacity-90 flex items-center gap-1">
                  <span className="w-2 h-2 bg-green-400 rounded-full"></span>
                  Online now
                </p>
              </div>
            </div>
            <button
              onClick={() => setIsOpen(false)}
              className="text-white hover:opacity-70 transition-opacity"
              data-testid="whatsapp-close-btn"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Body */}
          <div className="p-4 bg-[#ECE5DD] max-h-80 overflow-y-auto">
            <div className="bg-white p-3 rounded-lg shadow-sm mb-3">
              <p className="text-sm text-foreground">
                Hello! Welcome to Amjed Tours. How can we help you today?
              </p>
              <p className="text-xs text-muted-foreground mt-1">Support Team</p>
            </div>

            <p className="text-xs text-muted-foreground mb-2 mt-4">Quick messages:</p>
            <div className="space-y-2">
              {quickMessages.map((msg, index) => (
                <button
                  key={index}
                  onClick={() => openWhatsApp(msg)}
                  className="w-full text-left bg-white hover:bg-gray-50 p-3 rounded-lg text-sm border border-gray-200 transition-colors"
                  data-testid={`whatsapp-quick-msg-${index}`}
                >
                  {msg}
                </button>
              ))}
            </div>
          </div>

          {/* Input */}
          <div className="p-3 bg-white border-t border-border flex items-center gap-2">
            <input
              type="text"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && openWhatsApp()}
              placeholder="Type your message..."
              className="flex-1 px-3 py-2 text-sm border border-border rounded-full focus:outline-none focus:ring-2 focus:ring-primary/20"
              data-testid="whatsapp-message-input"
            />
            <button
              onClick={() => openWhatsApp()}
              className="w-10 h-10 bg-[#25D366] hover:bg-[#20BA5A] rounded-full flex items-center justify-center text-white transition-colors"
              data-testid="whatsapp-send-btn"
            >
              <Send className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Floating Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="fixed bottom-6 right-6 z-50 w-16 h-16 bg-[#25D366] hover:bg-[#20BA5A] rounded-full shadow-lg flex items-center justify-center text-white transition-all hover:scale-110"
        style={{ boxShadow: '0 8px 24px rgba(37, 211, 102, 0.4)' }}
        data-testid="whatsapp-float-btn"
        aria-label="Chat on WhatsApp"
      >
        {isOpen ? (
          <X className="w-7 h-7" />
        ) : (
          <svg viewBox="0 0 24 24" className="w-8 h-8 fill-current">
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
          </svg>
        )}
      </button>

      {/* Pulse animation */}
      {!isOpen && (
        <div 
          className="fixed bottom-6 right-6 z-40 w-16 h-16 rounded-full bg-[#25D366] opacity-40 pointer-events-none"
          style={{ animation: 'pulse-whatsapp 2s infinite' }}
        />
      )}

      <style jsx="true">{`
        @keyframes pulse-whatsapp {
          0% {
            transform: scale(1);
            opacity: 0.4;
          }
          50% {
            transform: scale(1.3);
            opacity: 0;
          }
          100% {
            transform: scale(1);
            opacity: 0;
          }
        }
      `}</style>
    </>
  );
};
